import { useEffect, useState } from 'react';
import { Drawer, Input, Button, List, Typography, Space, Popconfirm, Avatar, App } from 'antd';
import { DeleteOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import {
  useAnnotations,
  useCreateAnnotation,
  useUpdateAnnotation,
  useDeleteAnnotation,
} from '../../api/queries';
import { isApiError } from '../../api';
import type { AnnotationResponse } from '../../types/veridoc';
import type { DocAccessLevel } from '../../types';

const { Text } = Typography;
const { TextArea } = Input;

export interface ChangeAnnotationsPanelProps {
  open: boolean;
  jerryCmpId: string;
  changeId: string;
  currentUserId: string;
  accessLevel: DocAccessLevel;
  onClose: () => void;
}

export function ChangeAnnotationsPanel({
  open,
  jerryCmpId,
  changeId,
  currentUserId,
  accessLevel,
  onClose,
}: ChangeAnnotationsPanelProps) {
  const { message } = App.useApp();
  const canAnnotate = accessLevel !== 'view';
  const [newBody, setNewBody] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');

  // Reset all draft state when switching to a different change, so a half-typed
  // comment or open editor does not bleed across changes (the Drawer keeps this
  // component mounted; destroyOnHidden only clears the DOM, not these refs).
  useEffect(() => {
    setNewBody('');
    setEditingId(null);
    setEditingBody('');
  }, [changeId]);

  const { data, isLoading } = useAnnotations(jerryCmpId, changeId);
  const createMut = useCreateAnnotation(jerryCmpId);
  const updateMut = useUpdateAnnotation(jerryCmpId);
  const deleteMut = useDeleteAnnotation(jerryCmpId);

  const annotations: AnnotationResponse[] = data?.annotations ?? [];

  const handleCreate = () => {
    if (!newBody.trim()) return;
    createMut.mutate(
      { changeId, body: newBody.trim() },
      {
        onSuccess: () => setNewBody(''),
        onError: (err) => {
          const text = isApiError(err) ? err.message : '发表批注失败';
          message.error(text);
        },
      },
    );
  };

  const handleSaveEdit = (annotationId: string) => {
    if (!editingBody.trim()) return;
    updateMut.mutate(
      { annotationId, body: editingBody.trim() },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditingBody('');
        },
        onError: (err) => {
          // Keep the editor open with the typed text so a failed save does not
          // lose the user's input — they can retry without retyping.
          const text = isApiError(err) ? err.message : '修改批注失败';
          message.error(text);
        },
      },
    );
  };

  const handleDelete = (annotationId: string) => {
    deleteMut.mutate({ annotationId }, {
      onError: (err) => {
        const text = isApiError(err) ? err.message : '删除批注失败';
        message.error(text);
      },
    });
  };

  return (
    <Drawer title="批注" open={open} onClose={onClose} size={420} destroyOnHidden>
      <List
        loading={isLoading}
        dataSource={annotations}
        renderItem={(anno) => {
          const isAuthor = anno.authorId === currentUserId;
          return (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={
                  <Space>
                    <Text strong>{anno.authorDisplayName ?? '匿名'}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {new Date(anno.createdAt).toLocaleString()}
                    </Text>
                  </Space>
                }
                description={
                  editingId === anno.id ? (
                    <Space orientation="vertical" style={{ width: '100%' }}>
                      <TextArea
                        value={editingBody}
                        onChange={(e) => setEditingBody(e.target.value)}
                        autoSize={{ minRows: 2 }}
                      />
                      <Space>
                        <Button
                          size="small"
                          type="primary"
                          loading={updateMut.isPending}
                          disabled={!editingBody.trim() || updateMut.isPending}
                          onClick={() => handleSaveEdit(anno.id)}
                        >
                          保存
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setEditingId(null);
                            setEditingBody('');
                          }}
                        >
                          取消
                        </Button>
                      </Space>
                    </Space>
                  ) : (
                    <Text>{anno.body}</Text>
                  )
                }
              />
              {canAnnotate && isAuthor && editingId !== anno.id && (
                <Space>
                  <Button
                    size="small"
                    type="text"
                    icon={<EditOutlined />}
                    aria-label="编辑"
                    onClick={() => {
                      setEditingId(anno.id);
                      setEditingBody(anno.body);
                    }}
                  />
                  <Popconfirm
                    title="删除批注"
                    description="确认删除？此操作不可撤销"
                    onConfirm={() => handleDelete(anno.id)}
                  >
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="删除" />
                  </Popconfirm>
                </Space>
              )}
            </List.Item>
          );
        }}
      />

      {canAnnotate && (
        <div style={{ marginTop: 16 }}>
          <TextArea
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            placeholder="添加批注…"
            autoSize={{ minRows: 3 }}
          />
          <Button
            type="primary"
            style={{ marginTop: 8 }}
            loading={createMut.isPending}
            disabled={!newBody.trim()}
            onClick={handleCreate}
          >
            提交
          </Button>
        </div>
      )}
    </Drawer>
  );
}

export default ChangeAnnotationsPanel;
